import { useCallback, useEffect, useState } from 'react';

import { useIsHorizontalMobilePhone } from '../../../hooks';
import { useSidebarSummaryContext } from '../SidebarSummary.context';
import { SidebarOpenReason } from '../types/SidebarOpenReason';

export const useSidebarBehaviour = () => {
  const { animation, state, selectedQuestion, config, summary, type } =
    useSidebarSummaryContext();

  const isHorizontalMobilePhone = useIsHorizontalMobilePhone();

  const [showComponent, setShowComponent] = useState(config.omitAnimations === true);
  const [isFullWidthEnabled, setIsFullWidthEnabled] = useState(
    config.autoExpandOnOpen ?? false,
  );
  const [showLastAnimation, setShowLastAnimation] = useState(false);

  useEffect(() => {
    if (state.isOpen) {
      if (state.reason === SidebarOpenReason.Response && !config.omitAnimations) {
        animation.start();
      } else {
        setShowComponent(true);
      }
    }
  }, [state, state.isOpen, animation, config, type]);

  const handleClose = () => {
    state.toggleOpen(false);
    setIsFullWidthEnabled(false);
  };

  const handleMaximizeMinimize = () => {
    setIsFullWidthEnabled(!isFullWidthEnabled);
  };

  const handleRefresh = async () => {
    await summary.fetch();
  };

  const handleAnimationEnd = (forceShowComponent = false) => {
    config.onAnimationEnd?.(type);

    if (!selectedQuestion) return;

    if (selectedQuestion.isLast === forceShowComponent) {
      setShowComponent(true);
    } else if (selectedQuestion.isLast) {
      animation.stop();
      setShowLastAnimation(true);

      animation.start();
    }
  };

  const setDefaultState = useCallback(() => {
    setShowComponent(config.omitAnimations === true);
    setIsFullWidthEnabled(config.autoExpandOnOpen ?? false);
    setShowLastAnimation(false);
  }, [config.autoExpandOnOpen, config.omitAnimations]);

  const handleSidebarTransitionEnd = () => {
    if (!state.isOpen) {
      state.onSidebarCloseTransitionEnd();
      setDefaultState();
    }
  };

  useEffect(() => {
    if (selectedQuestion) {
      setDefaultState();
    }
  }, [setDefaultState, selectedQuestion]);

  useEffect(() => {
    if (!state.isOpen) {
      setIsFullWidthEnabled(false);
    }
  }, [state.isOpen]);

  useEffect(() => {
    if (config.isMobile && !isHorizontalMobilePhone) {
      setIsFullWidthEnabled(false);
    }
  }, [config.isMobile, isHorizontalMobilePhone]);

  return {
    showComponent,
    showLastAnimation,
    isFullWidthEnabled,
    handleClose,
    handleMaximizeMinimize,
    handleRefresh,
    handleAnimationEnd,
    handleSidebarTransitionEnd,
  };
};
